import { useEffect, useRef } from "react";
import "./ShadowPunchAnimation.css";

export default function ShadowPunchAnimation({ playerRef, opponentRef, onComplete }) {
  const animRef = useRef(null);

  useEffect(() => {
    const attackerEl = playerRef.current;
    const defenderEl = opponentRef.current;
    const container = defenderEl.closest(".battle-container");
    
    if (!attackerEl || !defenderEl || !container) {
      onComplete();
      return;
    }

    // Get positions for defender
    const defenderRect = defenderEl.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    // Calculate center of defender sprite
    const frameWidth = 192;  // Width of each frame (960px total width ÷ 5 frames)
    const frameHeight = 192; // Height of the sprite
    const centerX = defenderRect.left - containerRect.left + defenderRect.width / 2;
    const centerY = defenderRect.top - containerRect.top + defenderRect.height / 2;

    // Initial attacker animation (slight scale and rotation before attack)
    attackerEl.style.transition = 'transform 0.2s ease-in-out';
    attackerEl.style.transform = 'scale(1.1) rotate(-5deg)';
    
    // Create and position animation element
    const shadowPunch = document.createElement("div");
    shadowPunch.className = "shadow-punch-anim";
    shadowPunch.style.setProperty('--sx', `${centerX - frameWidth / 2}px`);
    shadowPunch.style.setProperty('--sy', `${centerY - frameHeight / 2}px`);
    container.appendChild(shadowPunch);
    animRef.current = shadowPunch;
    
    // Create impact effects at specific frame (frame 3)
    const effectTimer = setTimeout(() => {
      // Flash effect
      const flash = document.createElement('div');
      flash.className = 'shadow-punch-flash';
      flash.style.left = `${centerX}px`;
      flash.style.top = `${centerY}px`;
      container.appendChild(flash);
      
      // Make defender react
      defenderEl.classList.add('shadow-punch-hit');
      
      // Remove flash and reset defender
      setTimeout(() => {
        flash.remove();
        defenderEl.classList.remove('shadow-punch-hit');
      }, 350);
      
      // Create shadow particles
      for (let i = 0; i < 12; i++) {
        const particle = document.createElement('div');
        particle.className = 'shadow-punch-particle';
        
        // Position around the impact point
        const angle = Math.random() * Math.PI * 2;
        const distance = 5 + Math.random() * 25;
        particle.style.left = `${centerX + Math.cos(angle) * distance}px`;
        particle.style.top = `${centerY + Math.sin(angle) * distance}px`;
        
        // Random rotation and scale
        particle.style.setProperty('--rotation', `${Math.random() * 360}deg`);
        particle.style.setProperty('--scale', `${0.5 + Math.random() * 1.0}`);
        
        container.appendChild(particle);
        
        // Auto-remove particles
        setTimeout(() => particle.remove(), 400 + Math.random() * 300);
      }
      
      // Create impact shockwave
      const shockwave = document.createElement('div');
      shockwave.className = 'shadow-punch-shockwave';
      shockwave.style.left = `${centerX}px`;
      shockwave.style.top = `${centerY}px`;
      container.appendChild(shockwave);
      
      // Auto-remove shockwave
      setTimeout(() => shockwave.remove(), 500);
      
    }, 300); // Sync with frame 3 impact

    // Reset attacker position after animation
    const resetTimer = setTimeout(() => {
      attackerEl.style.transition = 'transform 0.25s ease-out';
      attackerEl.style.transform = '';
    }, 450);

    // Complete animation and cleanup
    const completeTimer = setTimeout(() => {
      if (shadowPunch.parentNode) {
        shadowPunch.remove();
      }
      onComplete();
    }, 900); // Allow full 5-frame animation to complete

    // Cleanup function
    return () => {
      clearTimeout(effectTimer);
      clearTimeout(resetTimer);
      clearTimeout(completeTimer);
      
      if (attackerEl) {
        attackerEl.style.transition = '';
        attackerEl.style.transform = '';
      }
      
      if (defenderEl) {
        defenderEl.classList.remove('shadow-punch-hit');
      }
      
      if (shadowPunch.parentNode) {
        shadowPunch.remove();
      }
      
      // Remove any flash, particles or shockwave
      const elements = container.querySelectorAll('.shadow-punch-flash, .shadow-punch-particle, .shadow-punch-shockwave');
      elements.forEach(el => el.remove());
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

ShadowPunchAnimation.moveType = "ghost";
